<!-- src/admin/pages/AddUserPage.vue -->
<template>
    <div class="add-user-container">
        <header class="page-header">
            <h1>Add New User</h1>
            <router-link to="/admin" class="back-link">← Back to Dashboard</router-link>
        </header>

        <div class="form-card">
            <form @submit.prevent="onSubmit">
                <div class="form-group">
                    <label for="email">Email Address</label>
                    <input id="email" type="email" v-model="email" placeholder="new.user@example.com" required />
                </div>

                <div class="form-group">
                    <label for="displayName">Full Name</label>
                    <input id="displayName" type="text" v-model="displayName" placeholder="John Doe" />
                </div>

                <div class="form-group">
                    <label for="role">Role</label>
                    <select id="role" v-model="role" required>
                        <option value="">Select a role…</option>
                        <option value="admin">Admin</option>
                        <option value="payroll">Payroll</option>
                        <option value="superadmin">Super Admin</option>
                    </select>
                </div>

                <button type="submit" class="btn-submit" :disabled="submitting">
                    {{ submitting ? "Creating…" : "Create User" }}
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import Swal from "sweetalert2"
import {
    getFirestore,
    collection,
    addDoc,
    updateDoc,       // <-- import updateDoc
    serverTimestamp,
    query,
    where,
    getDocs,
} from "firebase/firestore"
export default {
    name: "AddUserPage",
    data() {
        return {
            email: "",
            displayName: "",
            role: "",
            submitting: false,
        }
    },
    methods: {
        // Generate a random invite token (alphanumeric string)
        generateInviteToken(length = 20) {
            const chars =
                "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
            let token = ""
            for (let i = 0; i < length; i++) {
                token += chars.charAt(Math.floor(Math.random() * chars.length))
            }
            return token
        },

        async onSubmit() {
            if (!this.email) {
                await Swal.fire({
                    icon: "warning",
                    title: "Email Required",
                    text: "Please enter a valid email address.",
                })
                return
            }

            if (!this.role) {
                await Swal.fire({
                    icon: "warning",
                    title: "Role Required",
                    text: "Please select a role.",
                })
                return
            }

            this.submitting = true

            try {
                const db = getFirestore()
                const usersCol = collection(db, "users")

                // Check for duplicate email (case-insensitive)
                const normalizedEmail = this.email.trim().toLowerCase()
                const dupSnap = await getDocs(
                    query(usersCol, where("email", "==", normalizedEmail))
                )
                let isDuplicate = !dupSnap.empty
                if (!isDuplicate) {
                    // Fallback: scan to catch mixed-case legacy entries
                    const allSnap = await getDocs(usersCol)
                    isDuplicate = allSnap.docs.some(
                        (d) =>
                            (d.data().email || "").trim().toLowerCase() ===
                            normalizedEmail
                    )
                }
                if (isDuplicate) {
                    await Swal.fire({
                        icon: "warning",
                        title: "Duplicate Email",
                        text: `A user with the email "${this.email}" already exists.`,
                    })
                    this.submitting = false
                    return
                }

                // ─────────────────────────────────────────
                // 1) Generate an inviteToken
                const inviteToken = this.generateInviteToken(24)

                // 2) Add an entry to Firestore 'users' collection
                //    We let Firestore auto-generate the document ID, which we'll store as `uid`.
                const userDoc = await addDoc(usersCol, {
                    email: normalizedEmail,
                    displayName: this.displayName,
                    role: this.role,
                    status: "invited",
                    inviteToken: inviteToken,
                    createdAt: serverTimestamp(),
                    lastLogin: null,
                    lastLogout: null,
                })

                // The Firestore-generated ID:
                const newUid = userDoc.id
                await updateDoc(userDoc, {
                    uid: newUid,
                })

                // ─────────────────────────────────────────

                // 4) Trigger your existing Lambda to send the “set password” email
                //    The Lambda expects: { recipient_email, subject, message }
                const lambdaUrl =
                    "https://qmqivl7jmyshlmtxjgqae5li540ejbeo.lambda-url.us-east-2.on.aws/"

                // Construct a subject and message. Embed the inviteToken so your user can
                // paste it into your “Set Password” page, or click a link if your front-end supports it.
                const emailSubject = "Welcome! Set Your Password"
                const emailMessage = `
Hello ${this.displayName || ""},

You have been invited to join RTH Group as an admin. Please copy the token below
and paste it into our “Set Password” page to finish creating your account:

Invite Token: ${inviteToken}

Or click the link below and paste the token when prompted:
https://www.rthgroupinc.com/createaccount?token=${inviteToken}

If you did not expect this, please ignore this email.

Thank you,
RTH Group Team
        `.trim()

                await fetch(lambdaUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        recipient_email: this.email,
                        subject: emailSubject,
                        message: emailMessage,
                    }),
                })

                // 5) Show success alert
                await Swal.fire({
                    icon: "success",
                    title: "User Created",
                    html: `
<p>A new user (<b>${this.email}</b>) has been created as a <b>${this.role}</b>.</p>
<p>An email has been sent so they can set their password.</p>
`,
                    confirmButtonText: "OK",
                })

                // 6) Clear the form
                this.email = ""
                this.displayName = ""
                this.role = ""
            } catch (error) {
                console.error("Error creating user:", error)
                await Swal.fire({
                    icon: "error",
                    title: "Something went wrong",
                    text: error.message || "Could not create the user.",
                })
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.add-user-container {
    max-width: 600px;
    margin: 2rem auto;
    padding: 1rem;
}

.page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
}

.page-header h1 {
    font-size: 1.5rem;
}

.back-link {
    font-size: 0.9rem;
    text-decoration: none;
    color: #5867dd;
}

.form-card {
    background: #ffffff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
}

.form-group label {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #374151;
}

.form-group input {
    padding: 0.5rem;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
}

.form-group select {
    padding: 0.5rem;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #fff;
    cursor: pointer;
}

.btn-submit {
    background-color: #5867dd;
    color: #fff;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
}

.btn-submit:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
}
</style>
